'use client'

import { useIsActiveHideVatPrice } from '@/feature/product/hooks/useIsActiveHideVatPrice'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { twMerge } from '@/lib/classname/twMerge'
import type {
  ProductBuyBox_ProductFragment,
  ProductBuyBox_ProductPriceAndStockFragment,
} from '@gql-generated/graphql'
import { ProductType } from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import type React from 'react'
import { useMemo } from 'react'
import { productBuyBoxAddToCartAtom } from '../../services/product.store'
import { getProductDisplayPrice } from '../../utils/productDisplayPrice'
import type { ProductAvailabilityIssueCardProps } from '../product-availability-issue-card/ProductAvailabilityIssueCard.client'
import { ProductAvailabilityIssueCard } from '../product-availability-issue-card/ProductAvailabilityIssueCard.client'
import { ProductPrice } from '../product-price/ProductPrice.client'
import { ProductTag } from '../product-tag/ProductTag.client'
import { ProductBuyBoxCharges } from './ProductBuyBoxCharges'
import { ProductBuyBoxEcoTaxes } from './ProductBuyBoxEcoTaxes'
import { ProductBuyBoxReferencePublicPrice } from './ProductBuyBoxPublicPrice'

type MergedProductType = ProductBuyBox_ProductFragment &
  Partial<ProductBuyBox_ProductPriceAndStockFragment>

type ProductBuyBoxPricesProps = {
  product: MergedProductType
  hasPrice: boolean
  availabilityIssue: ProductAvailabilityIssueCardProps['issue'] | null
  isPublicPriceEnabled: boolean
  favoritePOSPhoneNumber?: string
}

export function ProductBuyBoxPrices({
  product,
  hasPrice,
  availabilityIssue,
  isPublicPriceEnabled,
  favoritePOSPhoneNumber,
}: ProductBuyBoxPricesProps): React.ReactElement | null {
  const { price, ecoPart, charges, type } = product
  const packaging = product.stock?.packaging
  const isActiveHideVatPrice = useIsActiveHideVatPrice()
  const isIncludedTaxPriceDisplayed = Boolean(price?.inclTax) && !isActiveHideVatPrice
  const isAuthenticated = useIsAuthenticated()
  const hasEcoPart = Boolean(ecoPart?.deee || ecoPart?.pmcb || ecoPart?.furnishing)
  const { quantity: selectedQuantity } = useAtomValue(productBuyBoxAddToCartAtom)

  const displayPrice = useMemo(
    () => getProductDisplayPrice(product, { withErpPrice: type === ProductType.Article }),
    [product, type],
  )

  if (!hasPrice) {
    if ((isPublicPriceEnabled || isAuthenticated) && availabilityIssue) {
      return (
        <ProductAvailabilityIssueCard
          issue={availabilityIssue}
          favoritePOSPhoneNumber={favoritePOSPhoneNumber}
        />
      )
    }

    return null
  }

  return (
    <>
      {price?.tag && (
        <div className="absolute right-1 top-1">
          <ProductTag tag={price.tag} />
        </div>
      )}
      <div className={twMerge('flex flex-col gap-x-1', price?.tag && 'pt-1')}>
        <ProductPrice
          size="4xl"
          price={displayPrice.price}
          priceQuantity={displayPrice.priceQuantity}
          unitPrice={displayPrice.erpPrice}
          discount={{
            price: displayPrice.discountAmount,
            percentage: displayPrice.discountPercentage,
            tiersPrices: displayPrice.tiersPrices,
          }}
          type={type}
          packaging={packaging}
          inclTaxPrice={isIncludedTaxPriceDisplayed ? displayPrice.inclTaxPrice : 0}
          wrapperClassName={isIncludedTaxPriceDisplayed ? 'items-end' : 'items-baseline'}
          selectedQuantity={selectedQuantity}
        />

        {/* ReferencePublicPrice (formerly known as GrossPrice in Hydralians) */}
        {isAuthenticated && process.env.NEXT_PUBLIC_BRAND === 'hydralians' && (
          <ProductBuyBoxReferencePublicPrice product={product} />
        )}

        {/* EcoTaxes */}
        {hasEcoPart && ecoPart ? (
          <ProductBuyBoxEcoTaxes ecoTaxes={ecoPart} />
        ) : (
          <ProductBuyBoxCharges charges={charges} />
        )}
      </div>
    </>
  )
}
